import { FormEvent, useRef, useState } from 'react';
import { ModalGeneral } from '@salud-front/shared';
import styles from './app.module.css';

type DocumentType = {
  code: string;
  label: string;
};

type Pharmacy = {
  name: string;
  address: string;
  hours: string;
  manager: string;
};

type PharmacyManager = {
  name: string;
  logo: string;
};

type CsvPharmacyRow = {
  Observaciones: string;
  codigoSede: string;
  nombreSucursal: string;
  direccion: string;
  horario: string;
};

type PharmacyData = {
  pharmacies: Pharmacy[];
  managers: PharmacyManager[];
};

const documentTypes: DocumentType[] = [
  { code: '1', label: 'Cédula de ciudadanía' },
  { code: '4', label: 'Cédula de extranjería' },
  { code: '3', label: 'Tarjeta de identidad' },
  { code: '7', label: 'Registro civil' },
  { code: '5', label: 'Pasaporte' },
  { code: '6', label: 'Carné Diplomatico' },
  { code: '10', label: 'Salvoconducto' },
  { code: '14', label: 'Permiso Protección Temporal' },
];

const MANAGER_LOGOS: Record<string, string> = {
  'Eticos': '/salud/Style%20Library/webparts/redfarmacias/eticos.png',
  'Cruz Verde': '/salud/Style%20Library/webparts/redfarmacias/cruzverde.png',
  'Disfarma': '/salud/Style%20Library/webparts/redfarmacias/Disfarma.png',
  'Ladmedis': '/salud/Style%20Library/webparts/redfarmacias/Ladmedis.png',
  'Ramedicas': '/salud/Style%20Library/webparts/redfarmacias/Ramedicas.png',
  'Offimedicas': '/salud/Style%20Library/webparts/redfarmacias/Offimedicas.png',
};

const MANAGER_ORDER = ['Eticos', 'Cruz Verde', 'Disfarma', 'Ladmedis', 'Ramedicas', 'Offimedicas'];

function parseCsvText(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const records: string[][] = [];
  let i = 0;
  while (i < normalized.length) {
    const record: string[] = [];
    let endOfRecord = false;
    while (!endOfRecord && i < normalized.length) {
      let field = '';
      if (normalized[i] === '"') {
        i++;
        while (i < normalized.length) {
          if (normalized[i] === '"') {
            if (i + 1 < normalized.length && normalized[i + 1] === '"') {
              field += '"'; i += 2;
            } else { i++; break; }
          } else { field += normalized[i]; i++; }
        }
      } else {
        while (i < normalized.length && normalized[i] !== ',' && normalized[i] !== '\n') {
          field += normalized[i]; i++;
        }
      }
      record.push(field.trim());
      if (i < normalized.length) {
        if (normalized[i] === ',') { i++; }
        else if (normalized[i] === '\n') { i++; endOfRecord = true; }
      } else { endOfRecord = true; }
    }
    if (record.length > 1 || (record.length === 1 && record[0] !== '')) records.push(record);
  }
  return records;
}

let _csvCache: CsvPharmacyRow[] | null = null;
let _csvPromise: Promise<CsvPharmacyRow[]> | null = null;

type AppConfig = {
  apiConsultarAfiliadoUrl: string;
  apiFarmaciasCsvUrl: string;
};

let _config: AppConfig | null = null;

async function loadConfig(): Promise<AppConfig> {
  if (_config) return _config;
  const fallback: AppConfig = {
    apiConsultarAfiliadoUrl: 'https://corporativo.compensar.com/_vti_bin/ServiciosProveedores/ServicioProveedores.svc/ConsultarAfiliadoRest',
    apiFarmaciasCsvUrl: 'https://corporativo.compensar.com/salud/_api/web/getfilebyserverrelativeurl(\'/salud/Style%20Library/sedesfarmacia/SEDES_FARMACIAS.csv\')/$value'
  };
  try {
    // Resolve relative to this script file so it works in any SharePoint path
    const configUrl = new URL('/salud/Style%20Library/webparts/redfarmacias/config.prod.json', import.meta.url).href;
    const res = await fetch(configUrl);
    if (!res.ok) throw new Error('config.prod.json not found');
    _config = await res.json() as AppConfig;
    return _config;
  } catch {
    _config = fallback;
    return _config;
  }
}

function loadCsv(csvUrl: string): Promise<CsvPharmacyRow[]> {
  if (_csvCache) return Promise.resolve(_csvCache);
  if (!_csvPromise) {
    _csvPromise = fetch(csvUrl, {
      headers: { Accept: 'text/plain, */*' },
    })
      .then(res => res.text())
      .then(text => {
        const rows = parseCsvText(text).slice(1);
        const data = rows
          .filter(row => row.length >= 6)
          .map(row => ({ codigoSede: row[0], nombreSucursal: row[3], direccion: row[4], horario: row[5] }));
        _csvCache = data;
        return data;
      })
      .catch(() => { _csvPromise = null; return []; });
  }
  return _csvPromise;
}

function extractManagerName(nombreSucursal: string): string {
  const lower = nombreSucursal.toLowerCase();
  for (const name of MANAGER_ORDER) {
    if (lower.includes(name.toLowerCase())) return name;
  }
  return nombreSucursal;
}

type AffiliateData = {
  sedeCode: string | null;
  indicadorPrestacion: string;
  programa: string;
  capI08: string;
};

function pickField(obj: Record<string, unknown>, names: string[]): string {
  for (const name of names) {
    const val = obj[name];
    if (val !== undefined && val !== null) return String(val).trim();
  }
  return '';
}

function extractAffiliateData(payload: unknown): AffiliateData {
  const empty: AffiliateData = { sedeCode: null, indicadorPrestacion: '', programa: '', capI08: '' };
  if (!payload || typeof payload !== 'object') return empty;
  const obj = payload as Record<string, unknown>;

  // Try new API response format (with Result field)
  const result = obj.Result as Record<string, unknown> | undefined;
  if (result && typeof result === 'object') {
    const sedeCodeRaw = result.CodigoSede ?? result.codigoSede;
    const sedeCode = (sedeCodeRaw && Number(sedeCodeRaw) !== 0) ? String(sedeCodeRaw).trim() : null;

    const programasArr = result.Programas ?? result.programas;
    const programa = (Array.isArray(programasArr) && programasArr.length > 0)
      ? String(programasArr[0]).trim()
      : '';

    return {
      sedeCode,
      indicadorPrestacion: 'S',
      programa,
      capI08: 'S'
    };
  }

  // Fall back to old API response format (with ConsultarAfiliadoOut wrapper)
  const output = obj.ConsultarAfiliadoOut ?? obj.consultarAfiliadoOut;
  if (output && typeof output === 'object') {
    const out = output as Record<string, unknown>;

    // Scan ALL PROGRAMA entries to find the best one for pharmacy access:
    // Priority: first program with IndicadorPrestacion='S' that is not CE-only.
    const programaArr = out.PROGRAMA ?? out.Programa ?? out.programa;
    let indicadorPrestacion = '';
    let programa = '';
    let capI08 = '';
    if (Array.isArray(programaArr) && programaArr.length > 0) {
      // Try to find a non-CE, non-CM plan with IndicadorPrestacion 'S'
      const preferred = (programaArr as Record<string, unknown>[]).find(p => {
        const plan = pickField(p, ['PlanAutorizacion', 'planAutorizacion', 'Programa', 'programa', 'Plan', 'plan']).toUpperCase();
        const ind = pickField(p, ['IndicadorPrestacion', 'IndPrestacion', 'indicadorPrestacion', 'indPrestacion']).toUpperCase();
        return ind === 'S' && plan !== 'CE' && plan !== 'CM';
      });
      const chosen = (preferred ?? programaArr[0]) as Record<string, unknown>;
      indicadorPrestacion = pickField(chosen, ['IndicadorPrestacion', 'IndPrestacion', 'indicadorPrestacion', 'indPrestacion']);
      programa = pickField(chosen, ['PlanAutorizacion', 'planAutorizacion', 'Programa', 'programa', 'Plan', 'plan']);
      capI08 = pickField(chosen, ['CapI08', 'capI08', 'CapIDB', 'capIDB', 'Cap08', 'cap08']);
    }

    // CodigoSede lives in Ubicacion array.
    // CodigoSede can be 0 when no specific sede is assigned; fall back to CapitaGeneral.
    const ubicacionArr = out.Ubicacion ?? out.ubicacion;
    let sedeCode: string | null = null;
    if (Array.isArray(ubicacionArr) && ubicacionArr.length > 0) {
      const ubic = ubicacionArr[0] as Record<string, unknown>;
      const rawSede = pickField(ubic, ['CodigoSede', 'CodSede', 'codigoSede', 'codSede']);
      const rawCapita = pickField(ubic, ['CapitaGeneral', 'capitaGeneral']);
      // Use CodigoSede if non-zero, otherwise use CapitaGeneral
      sedeCode = (rawSede && rawSede !== '0') ? rawSede : (rawCapita && rawCapita !== '0' ? rawCapita : null);
    }

    return { sedeCode, indicadorPrestacion, programa, capI08 };
  }

  return empty;
}

function canAccessPharmacies(data: AffiliateData): boolean {
  // IndicadorPrestacion must be 'S' when present
  if (data.indicadorPrestacion && data.indicadorPrestacion.toUpperCase() === 'N') return false;

  const prog = data.programa.toUpperCase();

  // CE has no pharmacy access
  if (prog === 'CE') return false;

  // PCE requires CapI08 = 'S'
  if (prog === 'PCE') {
    if (data.capI08 && data.capI08.toUpperCase() !== 'S') return false;
  }

  // SedeCode '0' or missing means no sede assigned
  if (!data.sedeCode || data.sedeCode === '0') return false;

  return true;
}

const noAffiliationMessage =
  'El documento ingresado no registra una afiliación activa a la EPS. Verifica la información e inténtalo nuevamente.';

function isAffiliateRetired(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;

  const response = payload as Record<string, unknown>;

  // Check for new API response format (with Result field)
  const result = response.Result ?? response.result;
  if (result && typeof result === 'object') {
    const resultData = result as Record<string, unknown>;
    const estados = resultData.Estados ?? resultData.estados;
    if (Array.isArray(estados)) {
      for (const estado of estados) {
        if (typeof estado === 'object' && estado !== null) {
          const value = (estado as Record<string, unknown>).Value ?? (estado as Record<string, unknown>).value;
          if (String(value).toLowerCase().trim() === 'retirado') {
            return true;
          }
        }
      }
    }
  }

  return false;
}

function hasAffiliateResult(payload: unknown) {
  if (!payload || typeof payload !== 'object') return false;

  const response = payload as Record<string, unknown>;
  const responseCode = response.ResponseCode ?? response.responseCode;

  if (responseCode !== undefined && Number(responseCode) !== 200) return false;

  // Check for new API response format (with Result field)
  const result = response.Result ?? response.result;
  if (result && typeof result === 'object') {
    const resultData = result as Record<string, unknown>;
    return Object.keys(resultData).length > 0;
  }

  // Fall back to old API response format
  const output = response.ConsultarAfiliadoOut ?? response.consultarAfiliadoOut;
  const errors = response.Errores ?? response.errores;
  if (Array.isArray(errors) && errors.length > 0) return false;
  if (typeof errors === 'string' && errors.trim().length > 0) return false;
  const basicData = response.DatosBasicos ?? response.datosBasicos;
  if (Array.isArray(basicData)) return basicData.length > 0;
  if (output !== undefined) return hasAffiliateResult(output);

  return false;
}



function PharmacyModalContent({ pharmacies, managers }: PharmacyData) {
  const [selectedManager, setSelectedManager] = useState(managers[0]);





  const visiblePharmacies = pharmacies.filter(p => p.manager === selectedManager?.name);

  return (
    <div className={styles.pharmacyModal}>
      <div className={styles.pharmacyModalHeader}>
        <h3>Consulta tu red de farmacias</h3>
        <p>Selecciona el gestor farmacéutico y conoce los puntos disponibles.</p>
        <div className={styles.managerTabs}>
          {managers.map((manager) => (
            <button
              className={selectedManager?.name === manager.name ? styles.activeTab : ''}
              key={manager.name}
              type="button"
              onClick={() => setSelectedManager(manager)}
            >
              <span>{manager.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={styles.managerTitle}>
        <h4>{selectedManager?.name}</h4>
        {selectedManager?.logo && <img src={selectedManager.logo} alt={`Logo de ${selectedManager.name}`} />}
      </div>
      <div className={styles.pharmacyGrid}>
        {visiblePharmacies.map((pharmacy, index) => (
          <article className={styles.pharmacy} key={`${pharmacy.name}-${index}`}>
            <h5>{pharmacy.name}</h5>
            <span className={styles.orangeRule} />
            <p><span aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg></span>{pharmacy.address}</p>
            <p><span aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg></span>{pharmacy.hours}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

const ALPHANUMERIC_DOC_TYPES = ['5', '6', '10']; // Pasaporte, Carné Diplomático, Salvoconducto

export function App() {
  const modalRef = useRef<ModalGeneral>(null);
  const [documentType, setDocumentType] = useState(documentTypes[0].code);
  const [documentNumber, setDocumentNumber] = useState('');
  const [alphanumericNumber, setAlphanumericNumber] = useState('');
  const [modalType, setModalType] = useState<'pharmacies' | 'empty' | 'notCovered' | 'noSedesFound'>('empty');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pharmacyData, setPharmacyData] = useState<PharmacyData>({ pharmacies: [], managers: [] });

  const isAlphanumericDocType = ALPHANUMERIC_DOC_TYPES.includes(documentType);

  const openModal = (type: 'pharmacies' | 'empty' | 'notCovered' | 'noSedesFound') => {
    setModalType(type);
    modalRef.current?.handleShow();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let docValue: string;
    if (isAlphanumericDocType) {
      docValue = alphanumericNumber.trim();
      if (!docValue) {
        setError('Ingresa tu número de documento.');
        return;
      }
    } else {
      docValue = documentNumber.trim();
      if (!docValue) {
        setError('Ingresa tu número de documento.');
        return;
      }
      if (docValue.length < 7 || docValue.length > 12) {
        setError('El número de documento debe tener entre 7 y 12 dígitos.');
        return;
      }
    }

    setError('');
    setLoading(true);
    try {
      const config = await loadConfig();
      const [response, csvRows] = await Promise.all([
        fetch(config.apiConsultarAfiliadoUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requestParam: {
              IdTipoIdentificacion: documentType,
              IdAfiliado: docValue,
            }
          })
        }),
        loadCsv(config.apiFarmaciasCsvUrl),
      ]);
      const payload = await response.json();
      if (response.ok && hasAffiliateResult(payload)) {
        if (isAffiliateRetired(payload)) {
          openModal('empty');
          return;
        }

        const affiliateData = extractAffiliateData(payload);
        if(affiliateData.sedeCode === null) {
          openModal('empty');
          return;
        }

        if (!canAccessPharmacies(affiliateData)) {
          openModal('notCovered');
        } else {
          const sedeCode = affiliateData.sedeCode ?? '';
          const filtered = csvRows.filter(row => row.codigoSede === sedeCode);
          if (filtered.length === 0) {
            openModal('noSedesFound');
           // openModal('empty');
          } else {
            // Build deduplicated manager list preserving MANAGER_ORDER
            const managerSet = new Set(filtered.map(row => extractManagerName(row.nombreSucursal)));
            const managers: PharmacyManager[] = MANAGER_ORDER
              .filter(name => managerSet.has(name))
              .map(name => ({ name, logo: MANAGER_LOGOS[name] ?? '' }));
            // Add any manager not in MANAGER_ORDER
            for (const name of managerSet) {
              if (!MANAGER_ORDER.includes(name)) managers.push({ name, logo: '' });
            }
            const pharmacies: Pharmacy[] = filtered.map(row => ({
              name: row.nombreSucursal,
              address: row.direccion,
              hours: row.horario,
              manager: extractManagerName(row.nombreSucursal),
            }));
            setPharmacyData({ pharmacies, managers });
            openModal('pharmacies');
          }
        }
      } else {
        openModal('empty');
      }
    } catch {
      openModal('empty');
    } finally {
      setLoading(false);
    }
  };

  const modalProps = modalType === 'pharmacies'
    ? {
        title: '',
        message: '',
        formattedText: <PharmacyModalContent pharmacies={pharmacyData.pharmacies} managers={pharmacyData.managers} />,
        icon: '',
        mainButton: '',
        altButton: '',
      }
    : modalType === 'notCovered'
    ? {
        title: 'Tu Plan Complementario te da mayor flexibilidad',
        message: '',
        formattedText: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.5 }}>
              Como afiliado al Plan Complementario puedes elegir cualquiera de las farmacias de la red autorizada para reclamar tus medicamentos.
            </p>
            <a
              href="/salud/plan-complementario/Paginas/red-de-farmacias.aspx"
              style={{
                display: 'inline-flex',
                minWidth: '122px',
                minHeight: '50px',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '28px',
                padding: '13px 32px',
                background: 'linear-gradient(110deg, #fb5a08, #f04408)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
              }}
            >
              Consultar red de farmacias
            </a>
          </div>
        ),
        icon: '',
        mainButton: '',
        altButton: '',
      }
    : modalType === 'noSedesFound'
    ? {
        title: 'No encontramos tu sede',
        message: 'No encontramos datos de sedes en el listado de las sedes disponibles. Por favor, comunícate con el servicio al cliente para validar tu información.',
        formattedText: undefined,
        icon: '',
        mainButton: 'Cerrar',
        altButton: '',
      }
    : {
        title: 'No encontramos una afiliación activa',
        message: noAffiliationMessage,
        formattedText: undefined,
        icon: '',
        mainButton: 'Cerrar',
        altButton: '',
      };


        


  return (
    <main className={styles.page}>
      <section className={styles.lookupPanel} aria-labelledby="page-title">
        <div className={styles.formColumn}>
          <h1 id="page-title">Consulta tu farmacia asignada</h1>
          <p className={styles.intro}>Ingresa tu tipo y número de documento para conocer la farmacia donde puedes reclamar tus medicamentos.</p>
          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="document-type">Tipo de documento <span>*</span></label>
            <div className={styles.selectWrap}>
              <select
                id="document-type"
                value={documentType}
                onChange={(event) => {
                  setDocumentType(event.target.value);
                  setDocumentNumber('');
                  setAlphanumericNumber('');
                  setError('');
                }}
              >
                {documentTypes.map((type) => <option key={type.code} value={type.code}>{type.label}</option>)}
              </select>
            </div>
            {isAlphanumericDocType ? (
              <>
                <label htmlFor="alphanumeric-number">Número de documento <span>*</span></label>
                <input
                  id="alphanumeric-number"
                  type="text"
                  value={alphanumericNumber}
                  onChange={(event) => setAlphanumericNumber(event.target.value.slice(0, 20))}
                  placeholder=""
                />
              </>
            ) : (
              <>
                <label htmlFor="document-number">Número de documento <span>*</span></label>
                <input
                  id="document-number"
                  inputMode="numeric"
                  minLength={7}
                  maxLength={12}
                  value={documentNumber}
                  onChange={(event) => setDocumentNumber(event.target.value.replace(/\D/g, '').slice(0, 12))}
                  placeholder=""
                />
              </>
            )}
            {error && <p className={styles.validationError} role="alert">{error}</p>}
            <button className={styles.submitButton} type="submit" disabled={loading}>
              {loading ? 'Consultando...' : 'Consultar'}
            </button>
          </form>
        </div>
        <div className={styles.imageColumn} aria-hidden="true">
          <div className={styles.pinkCircle} />
          <div className={`${styles.arc} ${styles.arcLeft}`} />
          <div className={`${styles.arc} ${styles.arcRight}`} />
          <span className={styles.orangeDot} />
          <img src="/salud/Style%20Library/webparts/redfarmacias/doctor-farmacia.png" alt="" />
        </div>
      </section>
      
      <ModalGeneral
        ref={modalRef}
        MessageTitle={modalProps.title}
        msg={modalProps.message}
        formattedText={modalProps.formattedText}
        Icono={modalProps.icon}
        colorIco="orange"
        NoX="modal-close"
        NoAceptar={modalProps.mainButton ? 'modal-primary' : 'hidden-button'}
        NoCancelar={modalProps.altButton ? 'modal-secondary' : 'hidden-button'}
        MainButtonText={modalProps.mainButton}
        AltButtonText={modalProps.altButton}
      />

    </main>
  );
}

export default App;
